import { useContext } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'

import { getPricingPlans } from 'ducks/billing'
import {
  getAnnualToggle,
  getSelectedPlanValue,
  getProratedCharge,
  getUnusedTimeAmount,
  getCouponAmount,
  getSubscription,
  getUpdatePaymentSettingsFlag,
} from 'ducks/trialWarning'
import {
  getPlanPrice,
  isCurrentPlanType,
  PRICE_TYPE_MONTHLY,
  PRICE_TYPE_YEARLY,
} from 'utils/billing.ts'
import { PlanChangeContext } from 'components/Shared/TrialWarning/PlanChangeContext'

const DISCOUNT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const useOrderSummaryData = ({ planName, coupon }) => {
  const { totalAddonsOverage } = useContext(PlanChangeContext)

  const annual = useSelector(getAnnualToggle)
  const pricingPlans = useSelector(getPricingPlans)
  const selectedPlanValue = useSelector(getSelectedPlanValue)
  const isPaymentMethodUpdateOnly = useSelector(getUpdatePaymentSettingsFlag)
  const proratedTotal = useSelector(getProratedCharge)
  const unusedTimeTotal = useSelector(getUnusedTimeAmount)
  const couponTotal = useSelector(getCouponAmount)
  const subscription = useSelector(getSubscription) || {}

  let printedPlanName
  let initialPlanCost
  let planCost
  let proratedNetAmount
  let yearlySavings

  const renderNewPlansPaymentForm =
    isCurrentPlanType(selectedPlanValue) && !isPaymentMethodUpdateOnly

  if (renderNewPlansPaymentForm) {
    const type = annual ? '12 Months' : 'Monthly'
    printedPlanName = `${planName} ${type}`

    const monthlyPlanCharge = getPlanPrice(
      selectedPlanValue,
      pricingPlans,
      PRICE_TYPE_MONTHLY
    )

    const yearlyPlanCharge = getPlanPrice(
      selectedPlanValue,
      pricingPlans,
      PRICE_TYPE_YEARLY
    )

    planCost = annual ? yearlyPlanCharge : monthlyPlanCharge
    initialPlanCost = annual ? monthlyPlanCharge * 12 : monthlyPlanCharge

    const yearlySavingsFmt = DISCOUNT_FORMATTER.format(
      (initialPlanCost - yearlyPlanCharge) / initialPlanCost
    )

    yearlySavings = `-${yearlySavingsFmt}`
  } else {
    if (annual) {
      if (selectedPlanValue === 'pro' || selectedPlanValue === 'individual') {
        printedPlanName = 'Pro Plan 12 Months'
        initialPlanCost = 600
        planCost = 468
        yearlySavings = '-$132'
      } else {
        printedPlanName = 'Business Plan 12 Months'
        initialPlanCost = 2400
        planCost = 1788
        yearlySavings = '-$612'
      }
    } else {
      if (selectedPlanValue === 'pro' || selectedPlanValue === 'individual') {
        printedPlanName = 'Monthly Pro Plan'
        initialPlanCost = 50
        planCost = 50
      } else {
        printedPlanName = 'Monthly Business Plan'
        initialPlanCost = 200
        planCost = 200
      }
    }
  }

  let amountOff
  let nextBill = planCost

  if (typeof unusedTimeTotal === 'number') {
    proratedNetAmount = unusedTimeTotal
  }

  if (coupon) {
    amountOff = coupon.percentOff
      ? planCost * (coupon.percentOff / 100)
      : coupon.amountOff / 100

    planCost = planCost - amountOff
    planCost = parseFloat(planCost.toFixed(2))
    amountOff = amountOff.toFixed(2).replace('.00', '')
    if (planCost < 0) planCost = 0

    nextBill = planCost

    if (coupon.duration !== 'forever') {
      if (coupon.durationInMonths) {
        if (
          coupon.durationInMonths <= 1 ||
          (coupon.durationInMonths <= 12 && annual)
        ) {
          nextBill = initialPlanCost
        }
      } else {
        nextBill = initialPlanCost
      }
    }
  }

  planCost += totalAddonsOverage

  if (!annual) {
    nextBill += totalAddonsOverage
  }

  if (typeof proratedTotal === 'number') {
    if (proratedTotal < 0) {
      planCost = 0
    } else {
      planCost = proratedTotal
    }

    if (couponTotal) {
      amountOff = couponTotal
      amountOff = amountOff.toFixed(2).replace('.00', '')
    }
  }

  const isSubscriptionAnnual = subscription?.planInterval === 'year'
  const isNewSubscriptionPeriod = annual !== isSubscriptionAnnual
  let { nextBillDate } = subscription

  if (!nextBillDate || isNewSubscriptionPeriod) {
    if (annual) {
      const nextYearDate = new Date().setFullYear(new Date().getFullYear() + 1)
      nextBillDate = moment(nextYearDate).format('M/DD/YYYY')
    } else {
      const nextMonthDate = new Date().setMonth(new Date().getMonth() + 1)
      nextBillDate = moment(nextMonthDate).format('M/DD/YYYY')
    }
  }

  return {
    plan: {
      name: printedPlanName,
      initialCost: initialPlanCost,
      isAnnual: annual,
      yearlySavings,
    },
    priceAdjustments: {
      proratedNetAmount,
      amountOff,
    },

    billing: {
      planCost,
      nextBill,
      nextBillDate,
    },
  }
}

export default useOrderSummaryData
